import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import db from './firebase.config.js';
import firebase from 'firebase/app';
import 'firebase/firestore';

import venmoLogo from './Venmo_Logo_Blue.png';
import paypalLogo from './pp_logo.svg';
import cashappLogo from './Cash_app_logo.svg';

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    borderRadius: 12,
    marginTop: 6,
    width: 288,
    maxWidth: 'calc(100vw - 32px)',
    background: '#151519',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#f0f0f2',
    boxShadow: '0 12px 36px rgba(0,0,0,0.4)',
  },
  '& .MuiMenu-list': { padding: 6 },
  '& .MuiMenuItem-root': {
    minHeight: 60,
    borderRadius: 8,
    padding: '10px 12px',
    gap: 12,
    whiteSpace: 'normal',
    textDecoration: 'none',
    '&:hover, &.Mui-focusVisible': { backgroundColor: 'rgba(255,255,255,0.06)' },
  },
  '& .tip-provider-logo': { width: 26, height: 26, objectFit: 'contain', flexShrink: 0 },
  '& .tip-provider-copy': { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  '& .tip-provider-name': { fontSize: 14, fontWeight: 600 },
  '& .tip-provider-handle': { fontSize: 12, color: '#a2a2ad', overflowWrap: 'anywhere' },
  '& .tip-provider-action': { marginLeft: 'auto', color: '#a2a2ad', fontSize: 12, flexShrink: 0 },
  '& .tip-copy-feedback, & .tip-unavailable': {
    padding: '6px 12px 10px', fontSize: 12, lineHeight: 1.5, color: '#a2a2ad',
  },
  '& .tip-copy-input': {
    display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 8,
    padding: '8px 10px', background: '#0e0e11', border: '1px solid #34343b',
    borderRadius: 6, color: '#f0f0f2', font: 'inherit',
  },
});

const handleText = value => typeof value === 'string' ? value.trim() : '';
const username = (value, prefix) => {
  const handle = handleText(value).replace(prefix, '');
  // Handles are a single profile identifier, never a URL or an extra path.
  return /^[a-zA-Z0-9_-]+$/.test(handle) ? handle : '';
};

export default function TipMenu({ anchorEl = null, onClose = () => {}, driver_tips, uid, pax, pax_sesh_id }) {
  const open = Boolean(anchorEl);
  const venmo = username(driver_tips?.venmo, /^@/);
  const cashapp = username(driver_tips?.cashapp, /^\$/);
  const paypal = handleText(driver_tips?.paypal);
  const hasTips = Boolean(venmo || cashapp || paypal);
  const loggedOpen = useRef(false);
  const copyAttempt = useRef(0);
  const [copyState, setCopyState] = useState('idle');

  useEffect(() => {
    setCopyState('idle');
    copyAttempt.current += 1;
    return () => { copyAttempt.current += 1; };
  }, [open, paypal]);

  useEffect(() => {
    if (!open) {
      loggedOpen.current = false;
      return;
    }
    if (loggedOpen.current || !hasTips || !uid || !pax || !pax_sesh_id) return;
    loggedOpen.current = true;

    const recordSession = async () => {
      const passenger = db.collection('Passengers').doc(pax);
      const session = passenger.collection('sessions').doc(pax_sesh_id);
      if (!(await session.get()).exists) return;
      await Promise.all([
        passenger.update({ last_used: firebase.firestore.FieldValue.serverTimestamp() }),
        session.update({
          actions: firebase.firestore.FieldValue.arrayUnion('tips_clicked'),
          interactions: firebase.firestore.FieldValue.arrayUnion({ action: 'tips_clicked', time: Date.now() / 1000 }),
        }),
      ]);
    };
    const recordDriver = async () => {
      await db.collection('Users').doc(uid).update({ tip_clicks: firebase.firestore.FieldValue.increment(1) });
    };
    // Analytics must never delay or prevent opening a payment provider.
    Promise.all([recordSession(), recordDriver()]).catch(() => {});
  }, [open, hasTips, uid, pax, pax_sesh_id]);

  const copyPaypal = async () => {
    const attempt = ++copyAttempt.current;
    setCopyState('copying');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(paypal);
      if (attempt === copyAttempt.current) setCopyState('copied');
    } catch {
      if (attempt === copyAttempt.current) setCopyState('manual');
    }
  };

  return (
    <StyledMenu
      id="tip-popup-menu"
      elevation={0}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      MenuListProps={{ 'aria-labelledby': 'tip-tab-button' }}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
    >
      {venmo && (
        <MenuItem component="a" href={`https://venmo.com/${encodeURIComponent(venmo)}`} target="_blank" rel="noopener noreferrer" onClick={onClose} disableRipple>
          <img className="tip-provider-logo" src={venmoLogo} alt="" />
          <span className="tip-provider-copy"><span className="tip-provider-name">Venmo</span><span className="tip-provider-handle">@{venmo}</span></span>
          <span className="tip-provider-action" aria-hidden="true">↗</span>
        </MenuItem>
      )}
      {paypal && (
        <MenuItem onClick={copyPaypal} disabled={copyState === 'copying'} aria-label="Copy PayPal details" disableRipple>
          <img className="tip-provider-logo" src={paypalLogo} alt="" />
          <span className="tip-provider-copy"><span className="tip-provider-name">PayPal</span><span className="tip-provider-handle">{paypal}</span></span>
          <span className="tip-provider-action" aria-hidden="true">{copyState === 'copied' ? 'Copied' : 'Copy'}</span>
        </MenuItem>
      )}
      {paypal && (copyState === 'copied' || copyState === 'manual') && (
        <li className="tip-copy-feedback" role="presentation">
          <span role="status">{copyState === 'copied' ? 'Copied. Paste in PayPal to tip your driver.' : 'Copy unavailable. Select and copy these PayPal details:'}</span>
          {copyState === 'manual' && (
            <input
              className="tip-copy-input"
              aria-label="PayPal details to copy"
              readOnly
              value={paypal}
              onFocus={event => event.target.select()}
              onKeyDown={event => { if (event.key !== 'Escape') event.stopPropagation(); }}
            />
          )}
        </li>
      )}
      {cashapp && (
        <MenuItem component="a" href={`https://cash.app/$${encodeURIComponent(cashapp)}`} target="_blank" rel="noopener noreferrer" onClick={onClose} disableRipple>
          <img className="tip-provider-logo" src={cashappLogo} alt="" />
          <span className="tip-provider-copy"><span className="tip-provider-name">Cash App</span><span className="tip-provider-handle">${cashapp}</span></span>
          <span className="tip-provider-action" aria-hidden="true">↗</span>
        </MenuItem>
      )}
      {!hasTips && <li className="tip-unavailable" role="presentation">Your driver hasn’t added tipping details yet.</li>}
    </StyledMenu>
  );
}
